import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { List, X, ArrowRight } from "@phosphor-icons/react";
import EnquireDialog from "./EnquireDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/blogs", label: "Insights" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [settings, setSettings] = useState({});
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [enquireOpen, setEnquireOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/settings`).then((r) => setSettings(r.data)).catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const logoUrl = settings.logo_url || settings.organization_logo_url || settings.default_og_image;
  const siteTitle = settings.site_title || "Bonheur Pharma";

  return (
    <header
      data-testid="public-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-[#F7F8F6]/80 backdrop-blur-xl backdrop-saturate-150 border-b border-[#0F172A]/10 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
          : "bg-[#F7F8F6]/70 backdrop-blur-lg"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
          {logoUrl ? (
            <img src={logoUrl.startsWith("http") ? logoUrl : `${BACKEND}${logoUrl}`} alt={siteTitle} className="h-14 lg:h-20 w-auto object-contain" loading="eager" data-testid="brand-logo" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#C2410C] to-[#0EA5E9] flex items-center justify-center text-white font-bold text-lg" style={{ fontFamily: "'Manrope', sans-serif" }}>B</div>
              <div className="leading-tight">
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#4B5563]">Pharmaceuticals</div>
                <div className="font-bold text-[#1F2937] text-sm sm:text-base" style={{ fontFamily: "'Manrope', sans-serif" }}>{siteTitle}</div>
              </div>
            </div>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-sm font-medium text-[#374151] hover:text-[#C2410C] transition-colors duration-200 relative after:absolute after:bottom-[-6px] after:left-0 after:h-[2px] after:w-0 hover:after:w-full after:bg-[#C2410C] after:transition-all after:duration-300">
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:block">
          <button onClick={() => setEnquireOpen(true)} data-testid="header-cta"
            className="btn-fluid inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border border-[#0F172A] text-[#0F172A] text-sm font-semibold">
            Enquire <ArrowRight size={14} weight="bold" />
          </button>
        </div>

        <button onClick={() => setOpen(!open)} className="lg:hidden p-2 text-[#1F2937]" data-testid="mobile-menu-btn" aria-label="Menu">
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div className={`lg:hidden overflow-hidden transition-all duration-300 ${open ? "max-h-[480px] border-t border-[#E2E8F0]" : "max-h-0"}`} data-testid="mobile-nav">
        <nav className="px-4 py-4 space-y-1 bg-white">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
              className="block px-3 py-3 rounded-md text-sm font-medium text-[#1F2937] hover:bg-[#C2410C]/10 hover:text-[#C2410C] transition-colors duration-150">
              {n.label}
            </Link>
          ))}
          <button onClick={() => { setOpen(false); setEnquireOpen(true); }} data-testid="mobile-header-cta"
            className="mt-2 w-full block text-center px-5 py-3 rounded-full bg-[#C2410C] text-white text-sm font-semibold">
            Enquire
          </button>
        </nav>
      </div>
      <EnquireDialog open={enquireOpen} onClose={() => setEnquireOpen(false)} source="header" />
    </header>
  );
}
